import { useState } from "react";
import styled from "styled-components";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { Toggle } from "../../components/Toggle";
import { H3 } from "../../components/Heading";
import { Card, GrayCard } from "../../components/Card";
import {
  MatchAnnotation,
  ScreeningResultView,
} from "../IndividualView/ScreeningResultView";
import { queryClient } from "../../queryClient";
import { monitoringScreeningMatchesQueryKey } from "../../data/useMonitoringScreeningMatches";
import { monitoringActivityQueryKey } from "../../data/useMonitoringActivityView";
import {
  formatShortDate,
  isUnsignedActivity,
  MonitoringMatch,
  runHistoryNote,
} from "../../utils/monitoringScreening";
import {
  MonitoringAlertPanel,
  MonitoringRecordPanel,
} from "./MonitoringAlertPanel";
import { MonitoringTimelineRail } from "./MonitoringTimelineRail";
import { EvidenceStrip } from "./EvidenceStrip";
import {
  ALL_ACTIVITY,
  TimelineSelection,
  useMonitoringScreeningModel,
} from "./useMonitoringScreeningModel";

/**
 * The LexisNexis2 Monitoring record view.
 *
 * A timeline of alerts on the left, and the screening component on the right
 * showing what the selected entry contributed. The persistent matches are the
 * spine; the results moving through them over time are the work.
 */

/**
 * The timeline follows the officer down a long match list.
 *
 * It is navigation, so scrolling it away defeats it. The page's scroll container
 * is the `Container` in IndividualMonitoringView, and this sticks against that.
 * The containing block is the flex row below, which is as tall as the content
 * column, so there is room for it to travel.
 */
const StickyRail = styled(Box)`
  ${({ theme: { space } }) => `
    position: sticky;
    /* clear of the container's own top padding, so it does not sit hard against
       the topbar once it detaches */
    top: ${space[1]};
  `}
`;

interface Props {
  monitoringId: number | string;
  monitoring: any;
  activities: any[];
  legacyActivities: any[];
  /** Opens a legacy PEP & Sanctions or Adverse Media activity in its slider. */
  onSelectLegacyActivity: (activity: any) => void;
  isEntity: boolean;
  /** Owned by the page so the activity history table can select an alert too. */
  selection: TimelineSelection | null;
  onSelectTimelineEntry: (selection: TimelineSelection) => void;
}

export const MonitoringScreeningBody = ({
  monitoringId,
  monitoring,
  activities,
  legacyActivities,
  onSelectLegacyActivity,
  isEntity,
  selection,
  onSelectTimelineEntry,
}: Props) => {
  const model = useMonitoringScreeningModel(
    monitoringId,
    monitoring,
    activities,
    { selection, onSelect: onSelectTimelineEntry },
  );

  const {
    isLoading,
    screeningActivities,
    loadedActivities,
    notLoadedCount,
    loadAllActivities,
    hasUnloadedMatches,
    isMatchListWithheld,
    failedActivityIds,
    retryActivity,
    activeSelection,
    selectedActivity,
    selectedApprovalComment,
    selectTimelineEntry,
    lens,
    setLens,
    matches,
    inScope,
    visible,
    notInScope,
    records,
    counts,
    accumulatedCount,
    selectedEvidenceFor,
    selectEvidence,
  } = model;

  const invalidate = () => {
    queryClient.invalidateQueries(
      monitoringScreeningMatchesQueryKey(monitoringId),
    );
    screeningActivities.forEach((activity) =>
      queryClient.invalidateQueries(monitoringActivityQueryKey(activity.id)),
    );
  };

  const resultCountByActivityId = matches.reduce(
    (totals: Record<number, number>, match) => {
      match.contributions.forEach((contribution) => {
        totals[contribution.activityId] =
          (totals[contribution.activityId] || 0) + contribution.resultCount;
      });
      return totals;
    },
    {},
  );

  const annotations = visible.reduce(
    (map: Record<number, MatchAnnotation>, match: MonitoringMatch) => {
      map[match.id] = {
        badges: match.hasNewResults ? ["NEW RESULT"] : [],
        note: runHistoryNote(match),
      };
      return map;
    },
    {},
  );

  const awaitingSignOff = screeningActivities.filter(isUnsignedActivity).length;

  if (isLoading && matches.length === 0) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="40vh">
        <Loader />
      </Flex>
    );
  }

  if (screeningActivities.length === 0) {
    // a record can be on the LexisNexis2 provider and still hold only legacy
    // activities, either because it was rolled back or because none have landed
    // under the new check yet. The About cards and the history table cover that
    // case, so there is nothing for this section to say
    if (activities.length > 0) return null;

    return (
      <GrayCard mx="0px">
        <Paragraph mt={0} mb={0} color="gray.60">
          Monitoring is active. No alerts have been received yet.
        </Paragraph>
      </GrayCard>
    );
  }

  return (
    // stretch, not flex-start: the rail column has to be as tall as the content
    // column for the sticky element inside it to have anywhere to travel
    <Flex gap="24" alignItems="stretch">
      <Box width="220px" flexShrink={0}>
        {/* the heading stays outside the sticky element. Pinned, it had no
            background of its own and the header cards scrolled visibly behind
            the words */}
        <H3 mb={2}>Activity</H3>
        <StickyRail>
          <MonitoringTimelineRail
            activities={loadedActivities}
            selection={activeSelection}
            onSelect={selectTimelineEntry}
            matchCount={accumulatedCount}
            resultCountByActivityId={resultCountByActivityId}
            notLoadedCount={notLoadedCount}
            onLoadMore={loadAllActivities}
            legacyActivities={legacyActivities}
            onSelectLegacy={onSelectLegacyActivity}
          />
        </StickyRail>
      </Box>

      <Box flex={1} minWidth={0}>
        <H3 mb={2}>Screening results</H3>
        {/* the screening component expects to sit on a surface: in the mandate
            check the slider provides one, and on this page it did not, which
            left the whole right-hand column floating on the page background */}
        <Card mb={0} p={3}>
        {selectedActivity ? (
          <MonitoringAlertPanel
            activity={selectedActivity}
            returnedCount={
              inScope.filter((match) => !match.isExcluded).length
            }
            totalMatchCount={accumulatedCount}
          />
        ) : (
          <MonitoringRecordPanel
            counts={counts}
            alertCount={screeningActivities.length}
            awaitingSignOff={awaitingSignOff}
          />
        )}

        {failedActivityIds.length > 0 && (
          <GrayCard mx="0px" mb={3}>
            <Flex justifyContent="space-between" alignItems="center" gap="16">
              <Paragraph mt={0} mb={0} color="gray.60">
                {failedActivityIds.length} alert
                {failedActivityIds.length === 1 ? "" : "s"} could not be loaded.
              </Paragraph>
              <Button
                variant="text"
                size="small"
                onClick={() => failedActivityIds.forEach(retryActivity)}
              >
                Retry
              </Button>
            </Flex>
          </GrayCard>
        )}

        {hasUnloadedMatches && (
          <Paragraph mt={0} mb={3} fontSize={0} color="orange.50">
            This record holds more matches than one page returns. Some are not
            shown.
          </Paragraph>
        )}

        {isMatchListWithheld && (
          <Paragraph mt={0} mb={3} fontSize={0} color="orange.50">
            You don't have permission to view screening matches, so decisions
            shown are as recorded on each alert and may not be the latest.
          </Paragraph>
        )}

        {/* no heading here — ScreeningResultView already renders its own
            "Screening Matches" heading right above the match cards, the same
            one the mandate Screening check uses, and a second copy above the
            Information Used / Check Results box read as a duplicate. */}
        <Flex justifyContent="flex-end" alignItems="center" gap="16" mb={2}>
          {/* one lens over the page, not a switch per card. A toggle rather than
              two buttons because the options are not peers: one is a filter on
              the other. Never disabled, so it can always be switched back */}
          <Flex gap="8" alignItems="center">
            <Paragraph mt={0} mb={0} fontSize={0} color="gray.60">
              New results only
            </Paragraph>
            <Toggle
              isChecked={lens === "new"}
              onChange={() => setLens(lens === "new" ? "all" : "new")}
            />
          </Flex>
        </Flex>

        {/* a decision is a single compliance position and reads the same
            everywhere, so a historical run still shows today's decision */}
        {activeSelection !== ALL_ACTIVITY &&
          selectedActivity &&
          screeningActivities[0]?.id !== selectedActivity.id && (
            <Paragraph mt={0} mb={2} fontSize={0} color="gray.50">
              Decisions shown are current. The evidence below is from the run of{" "}
              {formatShortDate(selectedActivity.created_on)}.
            </Paragraph>
          )}

        {visible.length === 0 ? (
          <GrayCard mx="0px" mb={3}>
            <Paragraph mt={0} mb={0} color="gray.60">
              {lens === "new"
                ? "Nothing on this record is awaiting review."
                : "This alert returned no watchlist matches."}
            </Paragraph>
          </GrayCard>
        ) : (
          <ScreeningResultView
            records={records}
            status={selectedActivity?.status?.status}
            // under All activity no single alert is in scope, so there is no
            // check status to name
            statusText={
              selectedActivity
                ? undefined
                : `${counts.total} across all activity`
            }
            type={isEntity ? "entity" : undefined}
            onDecisionUpdated={invalidate}
            annotations={annotations}
            // same compact header treatment, and the same decision banner,
            // as the mandate Screening check.
            layout="split"
            approvalComment={selectedApprovalComment}
            // the surrounding Card already supplies horizontal padding, so
            // Section's own left/right padding would otherwise double up
            flush
            renderMatchEvidence={(rendered: any) => {
              const match = model.matchById.get(rendered?.id);
              if (!match) return null;
              return (
                <EvidenceStrip
                  match={match}
                  selected={selectedEvidenceFor(match)}
                  onSelect={(activityId) =>
                    selectEvidence(match.id, activityId)
                  }
                />
              );
            }}
          />
        )}

        {/* shown in place rather than behind a link: an officer must not be
            able to mistake one alert for the whole record */}
        {notInScope.length > 0 && (
          <NotInScopeGroup
            entries={notInScope}
            label={
              selectedActivity
                ? "not returned by this alert"
                : "not covered by the loaded alerts"
            }
            onJump={(activityId) => selectTimelineEntry(activityId)}
          />
        )}
        </Card>
      </Box>
    </Flex>
  );
};

const NotInScopeGroup = ({
  entries,
  label,
  onJump,
}: {
  entries: any[];
  label: string;
  onJump: (activityId: number) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box mt={2}>
      <Button
        variant="text"
        size="small"
        px={0}
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen ? "Hide" : "Show"} {entries.length} match
        {entries.length === 1 ? "" : "es"} {label}
      </Button>

      {isOpen && (
        <GrayCard mx="0px" mt={2}>
          {entries.map((entry, index) => (
            <Flex
              key={entry.id}
              justifyContent="space-between"
              alignItems="baseline"
              gap="16"
              py={1}
              borderBottom={index < entries.length - 1 ? "1px solid" : "none"}
              borderBottomColor="gray.20"
            >
              <Box flex={1} minWidth={0}>
                <Paragraph mt={0} mb={0} color="gray.80" fontWeight="500">
                  {/* no loaded alert carries this subject, so there is no name
                      to show until one is opened */}
                  {entry.name || entry.matchId || `Match ${entry.id}`}
                </Paragraph>
                {!entry.name && (
                  <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
                    {/* inherited from the initial screening, or on an alert
                        beyond the load limit, so no loaded payload carries a
                        name for it */}
                    No loaded alert carries this subject
                  </Paragraph>
                )}
              </Box>
              <Paragraph mt={0} mb={0} fontSize={0} color="gray.60">
                {entry.statusDisplay || "Unknown"}
              </Paragraph>
              <Box width="110px" flexShrink={0} textAlign="right">
                {entry.jumpToActivityId ? (
                  <Button
                    variant="text"
                    size="small"
                    px={0}
                    onClick={() => onJump(entry.jumpToActivityId)}
                  >
                    {formatShortDate(entry.lastSeen) || "View"}
                  </Button>
                ) : (
                  <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
                    -
                  </Paragraph>
                )}
              </Box>
            </Flex>
          ))}
        </GrayCard>
      )}
    </Box>
  );
};
